import { useMemo } from "react";
import { Controller } from "react-hook-form";
import useIsDesktop from "../../../hooks/useIsDesktop";
import countryList from "react-select-country-list";
import { Divider, Group, Stack, SimpleGrid, Center } from "@mantine/core";
import CustomDateInput from "../../ui/CustomDateInput/CustomDateInput";
import CustomSelect from "../../ui/CustomSelect/CustomSelect";
import CustomTextInput from "../../ui/CustomTextInput/CustomTextInput";
import { optionsFilterByLabel } from "../../../utils/optionsFilter";
import CustomModal from "../../ui/CustomModal/CustomModal";
import { CustomText } from "../../ui/CustomText/CustomText";
import CustomLoader from "../../ui/CustomLoader/CustomLoader";
import CustomButton from "../../ui/CustomButton/CustomButton";
import {
  MERCHANT_FIELDS,
  ADMIN_FIELDS,
  OBJECT_FIELDS,
} from "../config/merchantFieldConfig";
import ObjectSelect from "../components/ObjectSelect";
import {
  toSelectOptions,
  toStaticOptions,
  toCountryOptions,
} from "../utils/selectOptionHelpers";
import { merchantValidators } from "../utils/merchantValidators";
import { adaptValidator } from "../../../utils/rhfValidatorAdapter";

export const MerchantInstitutionModal = ({
  opened,
  onClose,
  onSubmit,
  mode = "create",
  control,
  modeRef,
  fieldsToShow = null,
  isSubmitting,
  disableSubmit,
  merchantDetailsLoading,
  merchantDetailsError,
  economicSectorsData = [],
  economicSectorsLoading,
  economicSectorsHasError = false,
  gendersData = [],
  gendersLoading,
  gendersHasError = false,
  productTransactionCodesData = [],
  productTransactionCodesLoading,
  productTransactionCodesHasError = false,
  productOwnershipData = [],
  productOwnershipLoading,
  productOwnershipsHasError = false,
  productCategoriesData = [],
  productCategoriesLoading,
  productCategoriesHasError = false,
}) => {
  const isDesktop = useIsDesktop();
  const isEdit = mode === "edit";

  const title = isEdit
    ? "Edit Merchant Institution"
    : "Add Merchant Institution";
  const buttonLabel = isEdit ? "Update" : "Create";

  const shouldShowLoader =
    merchantDetailsLoading && isEdit && !merchantDetailsError;
  const shouldShowError =
    merchantDetailsError && isEdit && !merchantDetailsLoading;

  const countryOptions = useMemo(
    () => toCountryOptions(countryList().getData()),
    [],
  );
  const economicSectorOptions = useMemo(
    () => toSelectOptions(economicSectorsData),
    [economicSectorsData],
  );
  const genderOptions = useMemo(
    () => toSelectOptions(gendersData),
    [gendersData],
  );
  const productTransactionCodeOptions = useMemo(
    () => toSelectOptions(productTransactionCodesData),
    [productTransactionCodesData],
  );
  const productOwnershipOptions = useMemo(
    () => toSelectOptions(productOwnershipData),
    [productOwnershipData],
  );
  const productCategoryOptions = useMemo(
    () => toSelectOptions(productCategoriesData),
    [productCategoriesData],
  );

  const shouldRenderField = (key) => {
    if (!fieldsToShow) return true;
    return fieldsToShow.includes(key);
  };

  const renderField = (key) => {
    if (!shouldRenderField(key)) return null;
    const config = MERCHANT_FIELDS[key];

    const commonProps = {
      label: config.label,
      placeholder: config.placeholder,
      readOnly: isEdit && isSubmitting,
      required: isEdit ? !!config.requiredInEdit : config.required,
      withAsterisk: isEdit ? !!config.requiredInEdit : config.required,
      disabled: isSubmitting,
    };

    if (config.type === "select") {
      const optionsMap = {
        country: countryOptions,
        merchantType: toStaticOptions(config.data),
        currency: toStaticOptions(config.data),
        economicSector: economicSectorOptions,
        gender: genderOptions,
        productTransactionCode: productTransactionCodeOptions,
        productOwnership: productOwnershipOptions,
        productCategory: productCategoryOptions,
      };
      const dataForKey = optionsMap[key] || toStaticOptions(config.data || []);

      const loadingMap = {
        economicSector: economicSectorsLoading,
        gender: gendersLoading,
        productTransactionCode: productTransactionCodesLoading,
        productOwnership: productOwnershipLoading,
        productCategory: productCategoriesLoading,
      };
      const errorMap = {
        economicSector: economicSectorsHasError,
        gender: gendersHasError,
        productTransactionCode: productTransactionCodesHasError,
        productOwnership: productOwnershipsHasError,
        productCategory: productCategoriesHasError,
      };

      const isDisabledDueToError = isEdit && errorMap[key];
      const errorTooltip = isDisabledDueToError
        ? `Cannot edit: Failed to load ${config.label.toLowerCase()}`
        : undefined;

      if (OBJECT_FIELDS.includes(key)) {
        return (
          <Controller
            key={key}
            name={key}
            control={control}
            rules={{
              validate: adaptValidator(merchantValidators[key], modeRef),
            }}
            render={({ field, fieldState }) => {
              let optionsWithCurrentValue = dataForKey;
              if (
                isEdit &&
                errorMap[key] &&
                field.value?.code &&
                field.value?.description
              ) {
                if (!dataForKey.some((opt) => opt.value === field.value.code)) {
                  optionsWithCurrentValue = [
                    { value: field.value.code, label: field.value.description },
                    ...dataForKey,
                  ];
                }
              }
              return (
                <ObjectSelect
                  {...commonProps}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                  options={optionsWithCurrentValue}
                  isLoading={loadingMap[key]}
                  disabled={isSubmitting || isDisabledDueToError}
                  title={errorTooltip}
                />
              );
            }}
          />
        );
      }

      return (
        <Controller
          key={key}
          name={key}
          control={control}
          rules={{ validate: adaptValidator(merchantValidators[key], modeRef) }}
          render={({ field, fieldState }) => {
            let optionsWithCurrentValue = dataForKey;
            if (
              isEdit &&
              errorMap[key] &&
              typeof field.value === "string" &&
              field.value
            ) {
              if (!dataForKey.some((opt) => opt.value === field.value)) {
                optionsWithCurrentValue = [
                  { value: field.value, label: field.value },
                  ...dataForKey,
                ];
              }
            }
            return (
              <CustomSelect
                {...commonProps}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                searchable
                lockOnSelect
                comboboxProps={{ withinPortal: false }}
                data={optionsWithCurrentValue}
                filter={optionsFilterByLabel}
                disabled={isSubmitting || isDisabledDueToError}
                title={errorTooltip}
              />
            );
          }}
        />
      );
    }

    if (config.type === "date") {
      return (
        <Controller
          key={key}
          name={key}
          control={control}
          rules={{ validate: adaptValidator(merchantValidators[key], modeRef) }}
          render={({ field, fieldState }) => (
            <CustomDateInput
              {...commonProps}
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
            />
          )}
        />
      );
    }

    return (
      <Controller
        key={key}
        name={key}
        control={control}
        rules={{ validate: adaptValidator(merchantValidators[key], modeRef) }}
        render={({ field, fieldState }) => (
          <CustomTextInput
            {...commonProps}
            type={config.type}
            value={field.value ?? ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
          />
        )}
      />
    );
  };

  return (
    <CustomModal
      opened={opened}
      onClose={onClose}
      centered
      radius="md"
      size="lg"
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      withCloseButton={!isSubmitting}
      overlayProps={{ backgroundOpacity: 0.5, blur: 2 }}
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          padding: 0,
          borderRadius: "10px",
          maxHeight: "80vh",
        },
      }}
    >
      <div
        style={{
          flex: "0 0 auto",
          padding: "1.5rem 2rem",
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "white",
          zIndex: 1,
        }}
      >
        <CustomText fw={600} size="lg" ta="center" c="primary">
          {title}
        </CustomText>
      </div>

      <div style={{ flex: "1 1 auto", overflowY: "auto", padding: "2rem" }}>
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <CustomText fw={600} size="md" c="dimmed">
              Merchant Institution Details
            </CustomText>

            {shouldShowLoader && (
              <Center h={120}>
                <Stack align="center" gap="xs">
                  <CustomLoader
                    color="primary"
                    text="Loading Merchant Profile..."
                  />
                </Stack>
              </Center>
            )}

            {shouldShowError && (
              <Center h={120}>
                <Stack align="center" gap="xs">
                  <CustomText size="sm" fw={500} color="error">
                    {merchantDetailsError && "Failed to load merchant profile."}
                  </CustomText>
                  <CustomText size="xs" color="dimmed">
                    Please try again later.
                  </CustomText>
                </Stack>
              </Center>
            )}

            {!shouldShowLoader && !shouldShowError && (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {Object.keys(MERCHANT_FIELDS)
                  .filter((key) => !ADMIN_FIELDS.includes(key))
                  .map(renderField)}
              </SimpleGrid>
            )}

            {!isEdit && (
              <>
                <Divider
                  label="Merchant Admin Details"
                  labelPosition="center"
                  my="md"
                />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  {ADMIN_FIELDS.map(renderField)}
                </SimpleGrid>
              </>
            )}

            <Group
              justify={isDesktop ? "flex-end" : "stretch"}
              grow={!isDesktop}
              mt="md"
            >
              <CustomButton
                variant="default"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </CustomButton>
              <CustomButton
                type="submit"
                disabled={disableSubmit}
                loading={isSubmitting}
              >
                {buttonLabel}
              </CustomButton>
            </Group>
          </Stack>
        </form>
      </div>
    </CustomModal>
  );
};
